import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { courses } from '../mockData';
import { Clock, PoundSterling, Award, ChevronDown, ChevronUp } from 'lucide-react';
import CourseEnquiryForm from '../components/CourseEnquiryForm';

const Courses = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);

  const toggleCourse = (courseId) => {
    setExpandedCourse(expandedCourse === courseId ? null : courseId);
  };

  // Generate Course Schema for all courses
  const courseSchemaList = courses.map(course => ({
    "@type": "Course",
    "name": course.title,
    "description": course.description,
    "provider": {
      "@type": "Organization",
      "name": "Hair Extensions Training Academy",
      "sameAs": "https://hairextensionstraining.academy"
    },
    "offers": {
      "@type": "Offer",
      "price": course.price ? course.price.replace(/[^0-9]/g, '') : "0",
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "onsite",
      "duration": course.duration || "1 Day"
    },
    "educationalCredentialAwarded": "CPD Accredited Certificate"
  }));

  const pageSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        "name": "Hair Extensions Training Courses",
        "description": "CPD-accredited professional hair extensions and hair loss training courses",
        "numberOfItems": courses.length,
        "itemListElement": courseSchemaList.map((course, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": course
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://hairextensionstraining.academy/" },
          { "@type": "ListItem", "position": 2, "name": "Courses", "item": "https://hairextensionstraining.academy/courses" }
        ]
      }
    ]
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>Hair Extensions Courses | CPD Accredited Training | Hair Extensions Training Academy</title>
        <meta name="description" content="Browse our CPD-accredited hair extensions courses. From 1-day technician training to advanced consultant certifications. Courses from £295. Over 4,100 students trained." />
        <meta name="keywords" content="hair extensions course, hair extension training, CPD accredited, hair loss training, cranial prosthesis course, UK" />
        <link rel="canonical" href="https://hairextensionstraining.academy/courses" />
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      </Helmet>

      {/* Header Video */}
      <section className="courses-header-video">
        <video 
          src="https://customer-assets.emergentagent.com/job_a235b419-86b2-4667-9fd0-c39959dca4de/artifacts/pw7gm9h7_default_89b1cef0-3c05-487c-ba2f-8be96da3cdfc_0.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="courses-hero-video"
        />
      </section>

      {/* Hero Section */}
      <section className="courses-hero">
        <div className="content-wrapper">
          <h1 className="page-title">Our Courses</h1>
          <p className="page-subtitle">
            CPD Accredited Training Programmes for Hair Extensions & Hair Loss Solutions
          </p>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="section-container">
        <div className="content-wrapper">
          <a
            href="https://www.qualiquiz.app/quiz/which-hair-extension-course-is-right-for-you-take-60-second-assessment"
            target="_blank"
            rel="noopener noreferrer"
            className="courses-quiz-banner"
            data-testid="courses-quiz-banner"
            aria-label="Take the 60-second career assessment: Which hair extension technique should you learn first?"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_e0838653-edb4-4055-aeea-30513770ad02/artifacts/fqz9jszw_WhatsApp%20Image%202026-06-30%20at%2017.49.06%20%284%29.jpeg"
              alt="Which hair extension technique should you learn first? Take the 60-second career assessment."
              className="courses-quiz-banner-image"
              loading="lazy"
            />
          </a>

          <div className="courses-list">
            {courses.map((course) => (
              <div key={course.id} className="course-card">
                <div className="course-header">
                  <h3 className="course-title">{course.title}</h3>
                  <div className="course-meta">
                    <span className="course-meta-item">
                      <Clock size={16} />
                      {course.duration}
                    </span>
                    <span className="course-meta-item">
                      <PoundSterling size={16} />
                      {course.cost}
                    </span>
                    <span className="course-badge">
                      <Award size={16} />
                      {course.accreditation}
                    </span>
                  </div>
                </div>

                <p className="course-description">{course.description}</p>

                <button
                  className="course-expand-btn"
                  onClick={() => toggleCourse(course.id)}
                >
                  {expandedCourse === course.id ? 'Hide Details' : 'View Details'}
                  {expandedCourse === course.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>

                {expandedCourse === course.id && (
                  <div className="course-details">
                    <div className="course-section">
                      <h4 className="course-section-title">Learning Outcomes</h4>
                      <ul className="course-list">
                        {course.learningOutcomes.map((outcome, index) => (
                          <li key={index}>{outcome}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="course-section">
                      <h4 className="course-section-title">Course Breakdown</h4>
                      <p className="course-section-text">{course.courseBreakdown}</p>
                    </div>

                    <button
                      className="btn-primary"
                      onClick={() => setSelectedCourse(course)}
                    >
                      Enquire Now
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer Video */}
      <section className="courses-footer-video">
        <video 
          src="https://customer-assets.emergentagent.com/job_a235b419-86b2-4667-9fd0-c39959dca4de/artifacts/venqo3va_default_89b1cef0-3c05-487c-ba2f-8be96da3cdfc_0.mp4"
          autoPlay
          muted
          loop
          playsInline
          className="courses-footer-video-element"
        />
      </section>

      {/* Enquiry Form Modal */}
      {selectedCourse && (
        <div className="modal-overlay" onClick={() => setSelectedCourse(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <CourseEnquiryForm
              course={selectedCourse}
              onClose={() => setSelectedCourse(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;